import PT from 'prop-types';
import React, { useEffect, useState } from 'react';

import { getExcelExportSettings, saveExcelExportSettings } from '~/api/search';
import { EXCEL_EXPORT_SINGLE_FILE_DOC_COUNT_LIMIT } from '~/common/constants/max_lengths';
import EcFilterInput from '~/components/Shared/EcFilterInput/EcFilterInput';
import EcModalCard from '~/components/Shared/EcModalCard/EcModalCard';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { ExcelExportTabType } from '~/enums/ExcelExportTabType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { useIntl } from 'react-intl';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { getItemValue } from '~/utils/array';
import {
  combineValueInObject,
  excludeValueInObject,
  getObjectFlattenedValue,
} from '~/utils/object';
import { capitalizeWords, searchByString } from '~/utils/strings';

import FieldsCard from './FieldsCard/FieldsCard';
import { getSelectAllCheckboxStatus, isOneBooleanTrue } from './utils';

function ExportSettingModal({
  analyzerSearchQuery,
  title,
  totalDocs,
  totalDups,
  onExport,
  hideModal,
}) {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const enableClauseValueExportSettings = useFlag(
    FlagType.ClauseValueExportSettings,
  );
  const [excelExportOptionTabs, setExcelExportOptionTabs] = useState([]);
  const [excelExportSettingData, setExcelExportSettingData] = useState(null);
  const [filteredExcelExportSettingData, setFilteredExcelExportSettingData] =
    useState({});
  const [formData, setFormData] = useState({
    [ExcelExportTabType.Fields]: {},
    [ExcelExportTabType.Clauses]: [],
    [ExcelExportTabType.Tables]: [],
  });

  const [query, setQuery] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [isAllSelected, setAllSelected] = useState();
  const [enableDupDocs, setEnableDupDocs] = useState(false);
  const [selectedTab, setSelectedTab] = useState(ExcelExportTabType.All);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingUpdating, setIsExportingUpdating] = useState(false);

  const selectedItems = [
    ...getObjectFlattenedValue(formData[ExcelExportTabType.Fields]),
    ...formData[ExcelExportTabType.Clauses],
    ...formData[ExcelExportTabType.Tables],
  ];

  const labelText = intl.formatMessage({
    id: 'ExportSettingModal.includeDuplicatesLabel',
    defaultMessage: 'Include Duplicates',
    description: 'Label for include duplicates checkbox',
  });

  function getQueryPhrase() {
    return query
      ? intl.formatMessage(
          {
            id: 'ExportSettingModal.forQuery',
            defaultMessage: ' for {query}.',
            description:
              'Query phrase for search results. Example: query = "status:active"',
          },
          { query },
        )
      : '.';
  }

  function getConditionalSelectAllStatus() {
    return noResults ? false : isAllSelected;
  }

  function getSelectedItemsValue(array) {
    return array.filter((item) => item.selected).map((itm) => itm.value);
  }

  useEffect(() => {
    if (
      query &&
      !getObjectFlattenedValue(filteredExcelExportSettingData).length
    ) {
      setNoResults(true);
      setAllSelected(false);
    } else {
      setNoResults(false);
    }
  }, [selectedTab, query, filteredExcelExportSettingData]);

  useEffect(() => {
    getSelectAllCheckboxStatus(
      selectedTab,
      filteredExcelExportSettingData,
      formData,
      setAllSelected,
    );
  }, [filteredExcelExportSettingData, formData, selectedTab]);

  const { executor: saveExcelExportSetting } = useAsync(
    saveExcelExportSettings,
    {
      ...formData,
      [ExcelExportTabType.Fields]: getObjectFlattenedValue(
        formData[ExcelExportTabType.Fields],
      ),
      includeDupDocs: enableDupDocs,
    },
    {
      successHandler: () => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'ExportSettingModal.settingsSaved',
            defaultMessage:
              'Your settings have been saved for your next export.',
            description: 'Success message when export settings are saved',
          }),
        );
      },
      errorHandler: () =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'ExportSettingModal.saveError',
            defaultMessage:
              'An error occurred while saving excel export settings',
            description: 'Error message when saving export settings fails',
          }),
        ),
    },
  );

  const { isLoading } = useAsync(
    getExcelExportSettings,
    { query: analyzerSearchQuery },
    {
      condition: true,
      successHandler: (response) => processExcelSettings(response),
      errorHandler: () =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'ExportSettingModal.loadError',
            defaultMessage:
              'An error occurred while loading excel export settings',
            description: 'Error message when loading export settings fails',
          }),
        ),
    },
  );

  function processExcelSettings(response) {
    const fields = Object.entries(response.fields).map(([key, value]) => {
      return {
        sectionName: key,
        fields: value,
      };
    });
    const fieldsSelected = Object.fromEntries(
      Object.entries(response.fields).map(([key, val]) => [
        key,
        val.filter((itm) => itm.selected).map((d) => d.value),
      ]),
    );

    const data = {
      [ExcelExportTabType.Fields]: fields,
      [ExcelExportTabType.Clauses]: response.provisions,
      [ExcelExportTabType.Tables]: response.tables,
    };

    setExcelExportSettingData(data);
    setFilteredExcelExportSettingData(data);

    let options = [];

    if (Object.values(response.fields).length) {
      const fieldsTab = ExcelExportTabType.Fields;
      options.push({ label: capitalizeWords(fieldsTab), value: fieldsTab });
      setSelectedTab(fieldsTab);
    }

    if (response.provisions.length) {
      const clausesTab = ExcelExportTabType.Clauses;
      const label = enableClauseValueExportSettings
        ? 'Clauses'
        : capitalizeWords(clausesTab);
      options.push({ label, value: clausesTab });
    }

    if (!enableClauseValueExportSettings && response.tables.length) {
      const tablesTab = ExcelExportTabType.Tables;
      options.push({ label: capitalizeWords(tablesTab), value: tablesTab });
    }

    if (options.length > 1) {
      const allTab = ExcelExportTabType.All;
      options.unshift({ label: capitalizeWords(allTab), value: allTab });
      setSelectedTab(allTab);
    }

    setExcelExportOptionTabs(options);
    setEnableDupDocs(response.includeDupDocs || false);
    setFormData({
      [ExcelExportTabType.Fields]: fieldsSelected,
      [ExcelExportTabType.Clauses]: getSelectedItemsValue(response.provisions),
      [ExcelExportTabType.Tables]: getSelectedItemsValue(response.tables),
    });
  }

  function handleCheckboxClick(type, itemValue) {
    let newSelectedItems = formData?.[type] || [];

    if (newSelectedItems.includes(itemValue)) {
      newSelectedItems = newSelectedItems.filter((item) => item !== itemValue);
    } else {
      newSelectedItems.push(itemValue);
    }
    setFormData({ ...formData, [type]: newSelectedItems });
  }

  function onFieldSelect(sectionName, fieldIds) {
    const newFields = {};
    newFields[sectionName] = fieldIds;
    const newFormData = { ...formData.fields, ...newFields };
    setFormData({ ...formData, [ExcelExportTabType.Fields]: newFormData });
  }

  function handleSearch(searchString, tab) {
    setSelectedTab(tab);
    setQuery(searchString);

    if (!searchString) {
      setFilteredExcelExportSettingData(excelExportSettingData);
    } else {
      const filteredFields = excelExportSettingData.fields
        .map((item) => {
          let results = [];
          item.fields.forEach((itm) => {
            if (searchByString(itm.label, searchString)) {
              results.push(itm);
            }
            return results;
          });
          if (results.length) {
            return {
              sectionName: item.sectionName,
              fields: results,
            };
          } else if (searchByString(item.sectionName, searchString)) {
            return item;
          }
          return null;
        })
        .filter((item) => !!item);
      const filterProvisions = excelExportSettingData.provisions.filter(
        (item) => searchByString(item.label, searchString),
      );
      const filteredTables = excelExportSettingData.tables.filter((item) =>
        searchByString(item.label, searchString),
      );
      const dataShowWithTab = {};
      if (
        isOneBooleanTrue(
          tab === ExcelExportTabType.All,
          tab === ExcelExportTabType.Fields,
        )
      ) {
        dataShowWithTab[ExcelExportTabType.Fields] = filteredFields;
      }
      if (
        isOneBooleanTrue(
          tab === ExcelExportTabType.All,
          tab === ExcelExportTabType.Clauses,
        )
      ) {
        dataShowWithTab[ExcelExportTabType.Clauses] = filterProvisions;
      }
      if (
        !enableClauseValueExportSettings &&
        isOneBooleanTrue(
          tab === ExcelExportTabType.All,
          tab === ExcelExportTabType.Tables,
        )
      ) {
        dataShowWithTab[ExcelExportTabType.Tables] = filteredTables;
      }
      setFilteredExcelExportSettingData(dataShowWithTab);
    }
  }

  function handleSelectAllClick() {
    setAllSelected(!isAllSelected);
    const newFields =
      selectedTab === ExcelExportTabType.All ||
      selectedTab === ExcelExportTabType.Fields
        ? filteredExcelExportSettingData.fields.reduce(
            (obj, item) => ({
              ...obj,
              [item.sectionName]: item.fields.map((itm) => itm.value),
            }),
            {},
          )
        : {};

    const provisionValues =
      selectedTab === ExcelExportTabType.All ||
      selectedTab === ExcelExportTabType.Clauses
        ? getItemValue(filteredExcelExportSettingData.provisions)
        : [];
    const tableValues =
      !enableClauseValueExportSettings &&
      (selectedTab === ExcelExportTabType.All ||
        selectedTab === ExcelExportTabType.Tables)
        ? getItemValue(filteredExcelExportSettingData.tables)
        : [];

    if (isAllSelected) {
      setFormData({
        [ExcelExportTabType.Fields]: excludeValueInObject(
          { ...formData.fields },
          newFields,
        ),
        [ExcelExportTabType.Clauses]: formData.provisions.filter(
          (itm) => !provisionValues?.includes(itm),
        ),
        [ExcelExportTabType.Tables]: formData.tables.filter(
          (itm) => !tableValues.includes(itm),
        ),
      });
    } else {
      setFormData({
        [ExcelExportTabType.Fields]: combineValueInObject(
          { ...formData.fields },
          newFields,
        ),
        [ExcelExportTabType.Clauses]: [
          ...new Set([...formData.provisions, ...provisionValues]),
        ],
        [ExcelExportTabType.Tables]: [
          ...new Set([...formData.tables, ...tableValues]),
        ],
      });
    }
  }

  function preventDefaultBubbling(e) {
    e.preventDefault();
  }

  function renderFields() {
    const shouldShowFields = isOneBooleanTrue(
      selectedTab === ExcelExportTabType.All,
      selectedTab === ExcelExportTabType.Fields,
    );
    const dataDisplay = filteredExcelExportSettingData.fields;
    if (!dataDisplay?.length || !shouldShowFields) {
      return null;
    }
    const fieldsData = dataDisplay.map((field) => {
      return (
        <div role="presentation" onClick={preventDefaultBubbling}>
          <FieldsCard
            key={`field_card_${field.sectionName}`}
            shouldCollapse={query.length === 0}
            field={field}
            fieldsChosen={formData.fields[field.sectionName]}
            onFieldSelect={onFieldSelect}
          />
        </div>
      );
    });

    return (
      <Box mt={3}>
        <Text color="black-alpha-75" variant="2xs-dense-caps">
          {intl.formatMessage({
            id: 'ExportSettingModal.fieldsLabel',
            defaultMessage: 'Fields',
            description: 'Label for fields section',
          })}
        </Text>
        <Box>{fieldsData}</Box>
      </Box>
    );
  }

  function renderList(name, data) {
    const shouldShowList = isOneBooleanTrue(
      selectedTab === ExcelExportTabType.All,
      selectedTab === name,
    );
    if (!data?.length || !shouldShowList) {
      return null;
    }
    const dataList = data.map((item, index) => {
      return (
        <FlexLayout
          key={`excel_export_${name}_list_${index}`}
          alignItems="center"
          px={2}
          py={2}
          space={2}
          onClick={preventDefaultBubbling}
        >
          <Checkbox
            label={item.label}
            option={{
              label: item.label,
              value: formData[name].includes(item.value),
            }}
            value={formData[name].includes(item.value)}
            onChange={() => handleCheckboxClick(name, item.value)}
          />
        </FlexLayout>
      );
    });

    return (
      <Box mt={3}>
        <Text color="black-alpha-75" variant="2xs-dense-caps">
          {enableClauseValueExportSettings
            ? intl.formatMessage({
                id: 'ExportSettingModal.clausesLabel',
                defaultMessage: 'Clauses',
                description: 'Label for clauses section',
              })
            : capitalizeWords(name)}
        </Text>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {dataList}
        </Box>
      </Box>
    );
  }

  function renderExcelExportMeta() {
    return (
      <FlexLayout
        pt={4}
        mt={4}
        justifyContent="space-between"
        sx={{ borderTop: 'border' }}
      >
        <FlexLayout flexDirection="column">
          <Text color="gray-700" variant="xs-dense">
            {intl.formatMessage({
              id: 'ExportSettingModal.selectedItemsLabel',
              defaultMessage: 'Selected Items:',
              description: 'Label for selected items count',
            })}
          </Text>{' '}
          <Text color="gray-700" variant="xs-dense-bold">
            {formatNumber(selectedItems.length, { decimal: 0 })}
          </Text>
        </FlexLayout>
        <FlexLayout flexDirection="column">
          <Text color="gray-700" variant="xs-dense">
            {intl.formatMessage({
              id: 'ExportSettingModal.totalDocumentsLabel',
              defaultMessage: 'Total Documents:',
              description: 'Label for total documents count',
            })}
          </Text>{' '}
          <Text color="gray-700" variant="xs-dense-bold">
            {formatNumber(totalDocs, { decimal: 0 })}
          </Text>
        </FlexLayout>
        <FlexLayout flexDirection="column">
          <Text color="gray-700" variant="xs-dense">
            {intl.formatMessage({
              id: 'ExportSettingModal.totalDuplicatesLabel',
              defaultMessage: 'Total Duplicates:',
              description: 'Label for total duplicates count',
            })}
          </Text>
          <Text color="gray-700" variant="xs-dense-bold">
            {formatNumber(totalDups - totalDocs, { decimal: 0 })}
          </Text>
        </FlexLayout>
        <FlexLayout flexDirection="column">
          <Text color="gray-700" variant="xs-dense">
            {intl.formatMessage({
              id: 'ExportSettingModal.includingDuplicatesLabel',
              defaultMessage: 'Including Duplicates:',
              description: 'Label for including duplicates count',
            })}
          </Text>{' '}
          <Text color="gray-700" variant="xs-dense-bold">
            {formatNumber(totalDups, { decimal: 0 })}
          </Text>
        </FlexLayout>
      </FlexLayout>
    );
  }

  function renderSearchBarSelectAll() {
    return (
      <FlexLayout
        pb={4}
        mt={4}
        space={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: 'border' }}
      >
        <EcFilterInput
          value={query}
          onChange={(str) => handleSearch(str, selectedTab)}
          onClearSearch={() => handleSearch('', selectedTab)}
          placeholder={intl.formatMessage({
            id: 'ExportSettingModal.searchPlaceholder',
            defaultMessage: 'Search',
            description: 'Placeholder text for search input',
          })}
        />

        <FlexLayout space={2} justifyContent="end" inline alignItems="center">
          <Checkbox
            option={{
              label: intl.formatMessage({
                id: 'ExportSettingModal.selectAllLabel',
                defaultMessage: 'Select All',
                description: 'Label for select all checkbox',
              }),
              value: isAllSelected,
            }}
            value={getConditionalSelectAllStatus()}
            onChange={handleSelectAllClick}
          />
        </FlexLayout>
      </FlexLayout>
    );
  }

  return (
    <EcModalCard
      title={title}
      content={
        <>
          {isLoading ? (
            <FlexLayout justifyContent="center">
              <LoadingSpinner size="medium" />
            </FlexLayout>
          ) : (
            <>
              {excelExportSettingData && excelExportOptionTabs.length && (
                <>
                  <>
                    <Box>
                      <Tabs
                        enableContentPadding={false}
                        selectedTab={selectedTab}
                        tabs={excelExportOptionTabs}
                        onSelectTab={setSelectedTab}
                      />
                    </Box>
                    {renderSearchBarSelectAll()}
                  </>

                  <Box sx={{ maxHeight: '30vh', overflowY: 'scroll' }}>
                    {renderFields()}
                    {renderList(
                      ExcelExportTabType.Clauses,
                      filteredExcelExportSettingData.provisions,
                    )}
                    {!enableClauseValueExportSettings &&
                      renderList(
                        ExcelExportTabType.Tables,
                        filteredExcelExportSettingData.tables,
                      )}
                    {noResults && (
                      <FlexLayout
                        sx={{ height: '30vh' }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray-600" variant="m-dense-bold">
                          {intl.formatMessage(
                            {
                              id: 'ExportSettingModal.noResultsFound',
                              defaultMessage: 'No results found {queryPhrase}',
                              description:
                                'Message when no search results are found. Example: queryPhrase = "status:active"',
                            },
                            {
                              queryPhrase: getQueryPhrase(),
                            },
                          )}
                        </Text>
                      </FlexLayout>
                    )}
                  </Box>
                </>
              )}
              {renderExcelExportMeta()}

              {totalDocs > EXCEL_EXPORT_SINGLE_FILE_DOC_COUNT_LIMIT && (
                <Alert enableIcon variant="warning">
                  <Text color="gray-800" variant="xs-dense-medium">
                    {intl.formatMessage({
                      id: 'ExportSettingModal.fileSizeWarning',
                      defaultMessage:
                        'Exports files are limited to 5GB. If your export exceeds this size, it will be split into multiple files.',
                      description: 'Warning about export file size limits',
                    })}
                  </Text>
                </Alert>
              )}
            </>
          )}
        </>
      }
      footer={
        <>
          <FlexLayout
            space={2}
            justifyContent="space-between"
            inline
            alignItems="center"
          >
            <label htmlFor="ExcelExportSetttings/IncludeDuplicatesCheckbox">
              <Text variant="xs-dense"></Text>
            </label>
            <Checkbox
              label={labelText}
              option={{
                label: labelText,
                value: enableDupDocs,
              }}
              value={enableDupDocs}
              onChange={() => setEnableDupDocs(!enableDupDocs)}
            />
          </FlexLayout>
          <Button
            isLoading={isExportingUpdating}
            text={intl.formatMessage({
              id: 'ExportSettingModal.exportUpdateButton',
              defaultMessage: 'Export & Update Settings',
              description: 'Button text to export and update settings',
            })}
            onClick={() => {
              setIsExportingUpdating(true);
              onExport({
                ...formData,
                includeDupDocs: enableDupDocs,
                [ExcelExportTabType.Fields]: getObjectFlattenedValue(
                  formData[ExcelExportTabType.Fields],
                ),
              });
              saveExcelExportSetting();
            }}
          />
          <Button
            isLoading={isExporting}
            text={intl.formatMessage({
              id: 'ExportSettingModal.exportButton',
              defaultMessage: 'Export',
              description: 'Button text to export',
            })}
            variant="primary"
            onClick={() => {
              setIsExporting(true);
              onExport({
                ...formData,
                includeDupDocs: enableDupDocs,
                [ExcelExportTabType.Fields]: getObjectFlattenedValue(
                  formData[ExcelExportTabType.Fields],
                ),
              });
            }}
          />
        </>
      }
      hideModal={hideModal}
    />
  );
}

ExportSettingModal.propTypes = {
  title: PT.string.isRequired,
  totalDocs: PT.number,
  totalDups: PT.number,
  onExport: PT.func.isRequired,
  hideModal: PT.func.isRequired,
};

export default ExportSettingModal;
